import { ChevronLeft, ChevronRight, ExternalLink, Instagram } from "lucide-react";
import { CountUp } from "@/components/CountUp";
import { FadeCarousel } from "@/components/FadeCarousel";
import { useState } from "react";
import { useCarousel } from "@/hooks/useCarousel";
import { Lightbox } from "@/components/Lightbox";
import { HIPPO_URL, testimonialImages, tradeImages } from "@/data/content";

export const MentorshipSection = () => {
  const testimonials = useCarousel(testimonialImages.length);
  const trades = useCarousel(tradeImages.length);
  const [lightbox, setLightbox] = useState(null);
  const closeLightbox = () => setLightbox(null);
  const { current: currentTestimonial, setCurrent: setCurrentTestimonial, next: nextTestimonial, prev: prevTestimonial } = testimonials;
  const { current: currentTrade, setCurrent: setCurrentTrade, next: nextTrade, prev: prevTrade } = trades;

  return (
    <div className="fade-in" data-testid="mentorship-section">
      {lightbox === "testimonial" && (
        <Lightbox images={testimonialImages} carousel={testimonials} alt="Témoignage" onClose={closeLightbox} />
      )}
      {lightbox === "trade" && (
        <Lightbox images={tradeImages} carousel={trades} alt="Trade résultat" onClose={closeLightbox} />
      )}
      <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
        <div>
          <h2 className="text-5xl sm:text-7xl font-bold mb-6 leading-tight" data-testid="mentorship-title">
            CRT x ICT
          </h2>
          <p className="text-lg sm:text-xl text-white/80 mb-8 leading-relaxed">
            Rejoignez notre mentorship et maîtrisez les concepts les plus avancés d'ICT combinés au CRT.
          </p>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-white rounded-full mt-2"></div>
              <p className="text-white/90">23 chapitres de formation</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-white rounded-full mt-2"></div>
              <p className="text-white/90">Explication détaillée de mes trades</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-white rounded-full mt-2"></div>
              <p className="text-white/90">Live + rediffusions</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-white rounded-full mt-2"></div>
              <p className="text-white/90">Groupe de discussion privé</p>
            </div>
          </div>

          <p className="text-sm text-white/60 italic mb-8">
            Et bien plus encore...
          </p>

          <a
            href="https://www.launchpass.com/700s-ict/mentorship"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-bold rounded-lg hover:bg-white/90 hover:scale-105 transition-all shadow-xl"
            data-testid="mentorship-join-button"
          >
            En savoir plus
            <ExternalLink size={20} />
          </a>
        </div>

        <div className="flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-white/5 rounded-2xl blur-xl"></div>
            <img 
              src={HIPPO_URL} 
              alt="HIPPO - Mentor 700S ICT" 
              className="relative w-full max-w-md rounded-2xl border-2 border-white/20 shadow-2xl"
              data-testid="hippo-image"
            />
          </div>
        </div>
      </div>

      {/* Dual Carousel Section - Testimonials & Trades side by side */}
      <div className="mt-16">
        <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto" style={{gridAutoRows: '1fr'}}>
          
          {/* Testimonials Carousel - Left */}
          <div className="flex flex-col h-full">
            <h3 className="text-xl sm:text-2xl font-bold mb-4 text-center h-16 flex items-center justify-center flex-shrink-0" data-testid="testimonials-title">
              Témoignages de notre communauté privée
            </h3>
            
            <div className="relative flex-1 flex flex-col min-h-0">
              {/* Screenshot Display */}
              <div className="relative bg-white/5 backdrop-blur-sm border-2 border-white/20 rounded-lg p-2 overflow-hidden aspect-[9/16] w-full touch-pan-y select-none cursor-zoom-in" {...testimonials.swipeHandlers} onClick={() => setLightbox("testimonial")} title="Agrandir" data-testid="testimonial-swipe-area">
                <div className="absolute inset-2">
                  <FadeCarousel
                    images={testimonialImages}
                    current={currentTestimonial}
                    alt="Témoignage"
                    imgClassName="rounded-md shadow-2xl"
                    testId="testimonial-image"
                  />
                </div>
              </div>

              {/* Navigation Arrows */}
              <div className="flex justify-center gap-2 mt-3 flex-shrink-0">
                <button
                  onClick={prevTestimonial}
                  className="p-1.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 hover:scale-110 transition-all"
                  data-testid="testimonial-prev-button"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="p-1.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 hover:scale-110 transition-all"
                  data-testid="testimonial-next-button"
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              {/* Progress Indicator */}
              <div className="flex justify-center gap-1 mt-2 flex-shrink-0">
                {testimonialImages.map((img, index) => (
                  <button
                    key={img}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`h-1 rounded-full transition-all ${
                      index === currentTestimonial ? "bg-white w-6" : "bg-white/30 w-1"
                    }`}
                    data-testid={`testimonial-dot-${index}`}
                  />
                ))}
              </div>

              {/* Counter */}
              <p className="text-center mt-2 text-white/60 text-xs flex-shrink-0">
                {currentTestimonial + 1} / {testimonialImages.length}
              </p>
            </div>
          </div>

          {/* Trades Carousel - Right */}
          <div className="flex flex-col h-full">
            <h3 className="text-xl sm:text-2xl font-bold mb-4 text-center h-16 flex items-center justify-center flex-shrink-0" data-testid="trades-title">
              Quelques-uns de mes trades
            </h3>
            
            {tradeImages.length > 0 ? (
              <div className="relative flex-1 flex flex-col min-h-0">
                {/* Trade Screenshot Display */}
                <div className="relative rounded-lg overflow-hidden border-2 border-amber-400/30 shadow-2xl aspect-[9/16] w-full touch-pan-y select-none cursor-zoom-in" {...trades.swipeHandlers} onClick={() => setLightbox("trade")} title="Agrandir" data-testid="trade-swipe-area">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-transparent pointer-events-none z-10"></div>
                  <FadeCarousel
                    images={tradeImages}
                    current={currentTrade}
                    alt="Trade résultat"
                    testId="trade-image"
                  />
                </div>

                {/* Navigation Arrows */}
                <div className="flex justify-center gap-2 mt-3 flex-shrink-0">
                  <button
                    onClick={prevTrade}
                    className="p-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 hover:bg-amber-400/20 hover:scale-110 transition-all"
                    data-testid="trade-prev-button"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={nextTrade}
                    className="p-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 hover:bg-amber-400/20 hover:scale-110 transition-all"
                    data-testid="trade-next-button"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>

                {/* Progress Indicator */}
                <div className="flex justify-center gap-1 mt-2 flex-shrink-0">
                  {tradeImages.map((img, index) => (
                    <button
                      key={img}
                      onClick={() => setCurrentTrade(index)}
                      className={`h-1 rounded-full transition-all ${
                        index === currentTrade ? "bg-amber-400 w-6" : "bg-amber-400/30 w-1"
                      }`}
                      data-testid={`trade-dot-${index}`}
                    />
                  ))}
                </div>

                {/* Counter */}
                <p className="text-center mt-2 text-amber-400/80 text-xs font-medium flex-shrink-0">
                  {currentTrade + 1} / {tradeImages.length}
                </p>
              </div>
            ) : (
              <div className="relative bg-white/5 backdrop-blur-sm border-2 border-amber-400/20 rounded-lg p-6 text-center flex-1 flex items-center justify-center min-h-[400px]">
                <p className="text-white/60 text-sm">
                  Screenshots de trades à venir...
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Instagram Stories Link */}
        <div className="text-center mt-8">
          <p className="text-white/60 text-sm mb-3">
            Encore plus de témoignages et de résultats dans nos stories à la une
          </p>
          <a
            href="https://instagram.com/700s_ict"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all group"
            data-testid="instagram-stories-link"
          >
            <Instagram size={20} className="text-pink-400 group-hover:scale-110 transition-transform" />
            <span className="font-medium">Voir nos stories Instagram</span>
            <ExternalLink size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        </div>

        {/* Stats Below Both Carousels */}
        <div className="flex justify-center gap-8 mt-12">
          <div className="text-center p-8 rounded-xl bg-white/5 border border-white/10">
            <h4 className="text-4xl font-bold mb-2"><CountUp value={245} testId="stat-members-count" /></h4>
            <p className="text-white/70">Membres</p>
          </div>
          <div className="text-center p-8 rounded-xl bg-white/5 border border-white/10">
            <h4 className="text-4xl font-bold mb-2"><CountUp value={55000} testId="stat-messages-count" /></h4>
            <p className="text-white/70">Messages dans le groupe privé</p>
          </div>
        </div>
      </div>
    </div>
  );
};
